import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Search, MapPin, Phone, Mail, Calendar, X, ArrowRight, ChevronDown, Bookmark, Grid, Tag, Globe } from 'lucide-react';
import { useAuth } from '../hooks/useAuth';
import { apiService } from '../services/api';
import { Business, Appointment, Notification, Service } from '../types';
import AnimatedLink from '../components/AnimatedLink';
import CustomerNavbar from '../components/CustomerNavbar';
import Footer from '../components/Footer';
import LoadingSpinner from '../components/LoadingSpinner';
import { searchBusinesses } from '../utils/searchUtils';

// Business Result Item Component
interface BusinessResultItemProps {
  business: Business;
  getLogoUrl: (shortName: string) => string;
  getBusinessInitials: (name: string) => string;
}

const BusinessResultItem: React.FC<BusinessResultItemProps> = ({
  business,
  getLogoUrl,
  getBusinessInitials
}) => {
  const [logoError, setLogoError] = useState(false);
  const logoUrl = getLogoUrl(business.shortName);

  return (
    <div className="py-4 border-b border-gray-200 last:border-b-0">
      <div className="flex items-start gap-4">
          {/* Business Logo */}
          <div className="flex-shrink-0">
            <div className="w-16 h-16 rounded-lg overflow-hidden bg-gray-100 flex items-center justify-center border border-gray-200">
              {!logoError ? (
                <img
                  src={logoUrl}
                  alt={`${business.name} Logo`}
                  className="w-full h-full object-cover"
                  onError={() => setLogoError(true)}
                />
              ) : (
                <div
                  className="w-full h-full flex items-center justify-center font-bold text-lg"
                  style={{
                    background: 'conic-gradient(from 180deg at 50% 50%, #facc15, #fde047, #f97316, #facc15)',
                    color: '#92400e'
                  }}
                >
                  {getBusinessInitials(business.name)}
                </div>
              )}
            </div>
          </div>

          {/* Business Content */}
          <div className="flex-1 min-w-0">
            {/* Business Name - Clickable Link */}
            <div className="mb-3">
              <h3 className="text-xl font-bold text-gray-900 mb-1">
                <AnimatedLink
                  to={`/${business.shortName}`}
                  className="text-gray-900 hover:text-blue-900 transition-colors"
                >
                  {business.name}
                </AnimatedLink>
              </h3>
            </div>

            {/* Description */}
            {business.description && (
              <p className="text-sm text-gray-700 mb-4 line-clamp-2">
                {business.description}
              </p>
            )}

            {/* Contact Information */}
            <div className="space-y-2 mb-4">
              {business.address && (
                <div className="flex items-start gap-2">
                  <MapPin className="w-4 h-4 text-gray-400 mt-0.5 flex-shrink-0" />
                  <p className="text-sm text-gray-900 font-medium line-clamp-1">{business.address}</p>
                </div>
              )}
              {business.phone && (
                <div className="flex items-center gap-2">
                  <Phone className="w-4 h-4 text-gray-400 flex-shrink-0" />
                  <p className="text-sm text-gray-900 font-medium">{business.phone}</p>
                </div>
              )}
            </div>

            {/* Action Buttons */}
            <div className="pt-3">
              <div className="flex flex-wrap gap-2">
                <AnimatedLink
                  to={`/${business.shortName}`}
                  className="aacore-btn-outlined"
                >
                  <Globe className="h-4 w-4" />
                  <span>Website</span>
                </AnimatedLink>
                <AnimatedLink
                  to={`/${business.shortName}/booking`}
                  className="aacore-btn-primary"
                >
                  <Calendar className="h-4 w-4" />
                  <span>Book Now</span>
                </AnimatedLink>
              </div>
            </div>
          </div>
        </div>
    </div>
  );
};

type CategoryFilter = 'visited' | 'all' | string; // string for category IDs

interface BusinessCategory {
  id: string;
  name: string;
  description?: string;
}

const SearchPage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const query = searchParams.get('q') || '';
  const categoryParam = searchParams.get('category') || '';
  const [searchTerm, setSearchTerm] = useState(query);
  const [businesses, setBusinesses] = useState<Business[]>([]);
  const [allBusinesses, setAllBusinesses] = useState<Business[]>([]);
  const [visitedBusinessIds, setVisitedBusinessIds] = useState<Set<string>>(new Set());
  const [filteredBusinesses, setFilteredBusinesses] = useState<Business[]>([]);
  const [businessServices, setBusinessServices] = useState<Map<string, Service[]>>(new Map());
  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingServices, setIsLoadingServices] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState<CategoryFilter>('all');
  const [isCategoryDropdownOpen, setIsCategoryDropdownOpen] = useState(false);
  const [businessCategories, setBusinessCategories] = useState<BusinessCategory[]>([]);
  const [unreadNotifications, setUnreadNotifications] = useState<Notification[]>([]);
  const { logout, user } = useAuth();
  const navigate = useNavigate();
  const desktopSearchInputRef = useRef<HTMLInputElement>(null);
  const mobileSearchInputRef = useRef<HTMLInputElement>(null);
  const categoryDropdownRef = useRef<HTMLDivElement>(null);

  const focusVisibleSearchInput = (selectText: boolean) => {
    const desktop = desktopSearchInputRef.current;
    const mobile = mobileSearchInputRef.current;

    const candidates = [desktop, mobile].filter(Boolean) as HTMLInputElement[];

    // Prefer the visible input (desktop or mobile), fall back to whichever exists.
    const visible = candidates.find((el) => el.offsetParent !== null);
    const input = visible || candidates[0];

    if (!input) return;
    if (input.disabled) return;

    try {
      input.focus({ preventScroll: true });
    } catch {
      input.focus();
    }

    if (selectText && input.value) {
      input.select();
    }
  };

  // Handle logout
  const handleLogout = async () => {
    try {
      await logout();
    } catch (error) {
      console.error('Logout error:', error);
    }
  };

  // Load visited businesses from appointments
  useEffect(() => {
    const loadVisitedBusinesses = async () => {
      if (!user?.id) {
        setVisitedBusinessIds(new Set());
        return;
      }

      try {
        const response = await apiService.getCustomerAppointments({ limit: 1000 });
        const appointments: Appointment[] = response.data || [];
        
        // Extract unique business IDs from appointments
        const businessIdSet = new Set<string>();
        appointments.forEach(appointment => {
          if (appointment.businessId) {
            businessIdSet.add(appointment.businessId);
          }
        });
        
        setVisitedBusinessIds(businessIdSet);
      } catch (error) {
        console.error('Error loading visited businesses:', error);
        setVisitedBusinessIds(new Set());
      }
    };

    loadVisitedBusinesses();
  }, [user?.id]);

  // Load notifications for navbar
  useEffect(() => {
    const loadNotifications = async () => {
      if (!user?.id) {
        setUnreadNotifications([]);
        return;
      }
      
      try {
        const response = await apiService.getNotifications();
        const notifications = response.data || [];
        const unread = notifications.filter((n: Notification) => !n.is_read);
        setUnreadNotifications(unread);
      } catch (error) {
        console.error('Error loading notifications:', error);
        setUnreadNotifications([]);
      }
    };
    
    loadNotifications();
  }, [user?.id]);

  // Set default category based on visited history or URL param (only once on initial load)
  const [hasSetDefaultCategory, setHasSetDefaultCategory] = useState(false);

  // Load business categories
  useEffect(() => {
    const loadBusinessCategories = async () => {
      try {
        const categories = await apiService.getBusinessCategories();
        // Sort categories: "Others" should be last
        const sortedCategories = [...categories].sort((a, b) => {
          if (a.name === 'Others') return 1;
          if (b.name === 'Others') return -1;
          return a.name.localeCompare(b.name);
        });
        setBusinessCategories(sortedCategories);
        
        // After categories are loaded, check if we need to set category from URL param
        if (categoryParam && !hasSetDefaultCategory) {
          // Validate that the category param is a valid category ID
          const isValidCategory = categories.some(cat => cat.id === categoryParam);
          if (isValidCategory) {
            setSelectedCategory(categoryParam);
            setHasSetDefaultCategory(true);
          } else if (categoryParam === 'all' || (user?.id && categoryParam === 'visited')) {
            setSelectedCategory(categoryParam);
            setHasSetDefaultCategory(true);
          } else {
            setHasSetDefaultCategory(true);
          }
        }
      } catch (error) {
        console.error('Error loading business categories:', error);
        setBusinessCategories([]);
        if (categoryParam && !hasSetDefaultCategory) {
          setHasSetDefaultCategory(true);
        }
      }
    };

    loadBusinessCategories();
  }, [categoryParam, hasSetDefaultCategory, user?.id]);

  // Load businesses
  useEffect(() => {
    const loadBusinesses = async () => {
      try {
        // Customer-facing: exclude expired businesses (but keep backend default unchanged elsewhere).
        const businessesData = await apiService.getBusinesses({ includeExpired: false });
        setAllBusinesses(businessesData);
        
        // Shuffle businesses for random display
        const shuffled = [...businessesData].sort(() => Math.random() - 0.5);
        setBusinesses(shuffled);
      } catch (error) {
        console.error('Error loading businesses:', error);
        setBusinesses([]);
        setAllBusinesses([]);
      } finally {
        setIsLoading(false);
      }
    };

    loadBusinesses();
  }, []);
  
  useEffect(() => {
    if (isLoading || hasSetDefaultCategory) return;
    
    // If category is in URL params, it will be set after categories load
    // Otherwise, if user is logged in and has visited businesses, default to 'visited'
    if (!categoryParam && user?.id && visitedBusinessIds.size > 0) {
      setSelectedCategory('visited');
      setHasSetDefaultCategory(true);
    } else if (!categoryParam) {
      setHasSetDefaultCategory(true);
    }
  }, [isLoading, visitedBusinessIds.size, hasSetDefaultCategory, user?.id, categoryParam]);

  // Reset to 'all' if user logs out and selectedCategory is 'visited'
  useEffect(() => {
    if (!user?.id && selectedCategory === 'visited') {
      setSelectedCategory('all');
    }
  }, [user?.id, selectedCategory]);

  // Update search term when query param changes
  useEffect(() => {
    setSearchTerm(query);
  }, [query]);

  // Load services for businesses when there's a search query
  useEffect(() => {
    const loadServicesForBusinesses = async () => {
      if (!query.trim() || allBusinesses.length === 0) {
        setBusinessServices(new Map());
        return;
      }

      setIsLoadingServices(true);
      try {
        const servicesMap = new Map<string, Service[]>();
        
        // Load services for all businesses (we'll filter after)
        const servicePromises = allBusinesses.map(async (business) => {
          try {
            const services = await apiService.getBusinessServices(business.id);
            if (services && services.length > 0) {
              servicesMap.set(business.id, services);
            }
          } catch (error) {
            // Silently fail for individual businesses
            console.error(`Error loading services for business ${business.id}:`, error);
          }
        });

        await Promise.all(servicePromises);
        setBusinessServices(servicesMap);
      } catch (error) {
        console.error('Error loading services:', error);
        setBusinessServices(new Map());
      } finally {
        setIsLoadingServices(false);
      }
    };

    loadServicesForBusinesses();
  }, [query, allBusinesses]);

  // Filter businesses based on category and search
  useEffect(() => {
    let filtered: Business[] = [];

    // First, filter by category
    if (selectedCategory === 'visited') {
      // Show only visited businesses
      filtered = allBusinesses.filter(business => visitedBusinessIds.has(business.id));
    } else if (selectedCategory === 'all') {
      // Show all businesses (randomized)
      filtered = [...allBusinesses].sort(() => Math.random() - 0.5);
    } else {
      // Filter by business category - check if the business has the selected category in its categories array
      filtered = allBusinesses.filter(business => {
        const businessCategories = business.categories || [];
        return businessCategories.includes(selectedCategory);
      });
      // Randomize the filtered results
      filtered = filtered.sort(() => Math.random() - 0.5);
    }

    // Then apply fuzzy search filter if query exists
    if (query.trim()) {
      // Enhance businesses with services for search
      const businessesWithServices = filtered.map(business => ({
        ...business,
        services: businessServices.get(business.id) || []
      }));
      
      filtered = searchBusinesses(businessesWithServices, query);
    }

    // Limit to 10 businesses if showing all categories and no search
    // Otherwise show all filtered results
    if (selectedCategory === 'all' && !query.trim() && filtered.length > 10) {
      filtered = filtered.slice(0, 10);
    }

    setFilteredBusinesses(filtered);
  }, [query, allBusinesses, selectedCategory, visitedBusinessIds, businessServices]);

  // Close category dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (categoryDropdownRef.current && !categoryDropdownRef.current.contains(event.target as Node)) {
        setIsCategoryDropdownOpen(false);
      }
    };

    if (isCategoryDropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isCategoryDropdownOpen]);

  // Focus search input on initial page load and after searches.
  // IMPORTANT: we render *two* inputs (desktop + mobile). We must focus the visible one.
  useEffect(() => {
    if (isLoading) return;

    const frameId = requestAnimationFrame(() => {
      // Let layout settle (navbar, results, etc.), then focus the visible input.
      setTimeout(() => {
        focusVisibleSearchInput(Boolean(query));
      }, 0);
    });

    return () => cancelAnimationFrame(frameId);
  }, [isLoading, query]);

  const handleSearch = () => {
    const params = new URLSearchParams();
    
    if (searchTerm.trim()) {
      params.set('q', searchTerm.trim());
    }
    
    // Include category in query params if it's not 'all' and not 'visited' (for non-logged-in users)
    if (selectedCategory !== 'all' && selectedCategory !== 'visited') {
      params.set('category', selectedCategory);
    } else if (selectedCategory === 'visited' && user?.id) {
      // Only include 'visited' if user is logged in
      params.set('category', 'visited');
    }
    
    const queryString = params.toString();
    navigate(`/search${queryString ? `?${queryString}` : ''}`);
    
    // Focus will happen automatically when query changes via useEffect above
  };

  const getCategoryLabel = (category: CategoryFilter): string => {
    if (category === 'visited') {
      return 'Visited';
    }
    if (category === 'all') {
      return 'All Categories';
    }
    // Find category by ID
    const foundCategory = businessCategories.find(cat => cat.id === category);
    return foundCategory ? foundCategory.name : 'All Categories';
  };

  const getCategoryIcon = (category: CategoryFilter) => {
    if (category === 'visited') {
      return <Bookmark className="h-4 w-4" />;
    }
    if (category === 'all') {
      return <Grid className="h-4 w-4" />;
    }
    // For business categories, use Tag icon
    return <Tag className="h-4 w-4" />;
  };

  const handleCategorySelect = (category: CategoryFilter) => {
    setSelectedCategory(category);
    setIsCategoryDropdownOpen(false);
    
    // Update URL with new category if there's a search term or if category is not 'all'
    const params = new URLSearchParams();
    if (searchTerm.trim()) {
      params.set('q', searchTerm.trim());
    }
    if (category !== 'all' && category !== 'visited') {
      params.set('category', category);
    } else if (category === 'visited' && user?.id) {
      params.set('category', 'visited');
    }
    
    const queryString = params.toString();
    navigate(`/search${queryString ? `?${queryString}` : ''}`, { replace: true });
  };

  const getEmptyStateMessage = () => {
    if (query.trim()) {
      return {
        title: 'No businesses found',
        message: "Try adjusting your search terms to find what you're looking for."
      };
    }
    if (selectedCategory === 'visited') {
      return {
        title: 'No visited businesses',
        message: "You haven't visited any businesses yet. Book your first appointment to see them here!"
      };
    }
    if (selectedCategory !== 'all') {
      const categoryName = getCategoryLabel(selectedCategory);
      return {
        title: `No businesses found in ${categoryName}`,
        message: `There are no businesses in the ${categoryName} category at the moment. Try selecting a different category or browse all businesses.`
      };
    }
    return {
      title: 'No businesses available',
      message: 'No businesses are currently available.'
    };
  };

  // Helper function to get logo URL
  const getLogoUrl = (shortName: string): string => {
    const apiBaseUrl = process.env.REACT_APP_API_URL || (process.env.REACT_APP_ENV === 'production' ? 'https://booking-i73o.onrender.com/api' : 'http://localhost:3001/api');
    return `${apiBaseUrl}/businesses/${shortName}/logo`;
  };

  // Helper function to get business initials for fallback
  const getBusinessInitials = (name: string): string => {
    return name
      .split(' ')
      .map(word => word[0])
      .join('')
      .toUpperCase()
      .slice(0, 2);
  };

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white">
        <LoadingSpinner />
      </div>
    );
  }

  const emptyState = getEmptyStateMessage();

  return (
    <div className="min-h-screen flex flex-col no-animations relative">
      {/* Customer Navbar */}
      <CustomerNavbar 
        onLogout={handleLogout}
        showDashboardLink={true}
        showLogoutButton={true}
        showAuthButtons={true}
        unreadNotificationsCount={unreadNotifications.length}
        onNotificationsClick={() => navigate('/dashboard/customer/notifications')}
      />
      
      <div className="flex-1 pt-6 pb-12">
        <div className="max-w-7xl mx-auto px-4 lg:px-8 relative z-10">
          {/* Header and Category Filter */}
          <div className="mb-4">
            {/* Page Header */}
            <div className="mb-4 text-center">
              <h1 className="text-xl font-bold mb-0">Book Service</h1>
              <p className="text-gray-600 text-sm mb-0">Find and book services from local businesses</p>
            </div>

            {/* Search Pill Group */}
            <form 
              className="flex justify-center mb-4" 
              onSubmit={(e) => {
                e.preventDefault();
                handleSearch();
              }}
              role="search"
            >
              <div className="w-full max-w-3xl">
                <div 
                  className="search-pill-group flex flex-col md:flex-row md:items-center gap-2 md:gap-0 rounded-xl md:rounded-full md:border md:bg-white md:shadow-sm"
                  style={{
                    ...(typeof window !== 'undefined' && window.innerWidth >= 768 ? {
                      border: '1px solid #e5e7eb',
                      background: '#fff',
                      boxShadow: '0 1px 2px rgba(0,0,0,.04)',
                    } : {}),
                    padding: '4px'
                  }}
                >
                  {/* Category Dropdown */}
                  <div className="dropdown relative w-full md:w-auto" ref={categoryDropdownRef}>
                    <button
                      type="button"
                      className="category-dropdown-btn dropdown-toggle w-full md:w-auto rounded-lg md:rounded-l-full border md:border-0 md:border-r md:border-gray-200"
                      onClick={() => setIsCategoryDropdownOpen(!isCategoryDropdownOpen)}
                      style={{
                        background: window.innerWidth < 768 ? '#fff' : '#f8fafc',
                        borderColor: window.innerWidth < 768 ? '#e5e7eb' : undefined,
                        padding: '.5rem 1rem',
                        fontSize: '.85rem',
                        fontWeight: '600',
                        color: '#0B4778',
                        whiteSpace: 'nowrap',
                        display: 'inline-flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        gap: '.35rem',
                        cursor: 'pointer'
                      }}
                      onMouseEnter={(e) => {
                        if (window.innerWidth >= 768) {
                          e.currentTarget.style.background = '#f1f5f9';
                        }
                      }}
                      onMouseLeave={(e) => {
                        if (window.innerWidth >= 768) {
                          e.currentTarget.style.background = '#f8fafc';
                        } else {
                          e.currentTarget.style.background = '#fff';
                        }
                      }}
                    >
                      <span className="flex items-center gap-2">
                        <i className="bi bi-grid-fill" aria-hidden="true"></i>
                        <span>{getCategoryLabel(selectedCategory)}</span>
                      </span>
                      <i className={`bi bi-chevron-down transition-transform ${isCategoryDropdownOpen ? 'rotate-180' : ''}`} style={{ fontSize: '.75rem' }}></i>
                    </button>
                    
                    {/* Dropdown Menu */}
                    {isCategoryDropdownOpen && (
                      <ul 
                        className="dropdown-menu shadow border-0 w-full md:w-auto md:min-w-[280px]"
                        style={{
                          borderRadius: '12px',
                          boxShadow: '0 4px 6px -1px rgba(0,0,0,.1), 0 2px 4px -1px rgba(0,0,0,.06)',
                          marginTop: '8px',
                          display: 'block',
                          position: 'absolute',
                          top: '100%',
                          left: '0',
                          zIndex: 1000,
                          background: '#fff',
                          border: '1px solid #e5e7eb',
                          listStyle: 'none',
                          padding: 0,
                          margin: 0,
                          minWidth: '200px'
                        }}
                      >
                        <li style={{ padding: '.5rem 1rem .25rem', fontSize: '.875rem', color: '#6b7280' }}>Popular categories</li>
                        {/* Only show "Visited" option if user is logged in */}
                        {user?.id && (
                          <li>
                            <button
                              type="button"
                              onClick={() => handleCategorySelect('visited')}
                              className="w-full text-left"
                              style={{
                                display: 'block',
                                width: '100%',
                                padding: '.5rem 1rem',
                                textAlign: 'left',
                                background: selectedCategory === 'visited' ? '#0B4778' : 'transparent',
                                color: selectedCategory === 'visited' ? '#fff' : '#111827',
                                border: 'none',
                                cursor: 'pointer',
                                fontSize: '.875rem',
                                fontWeight: selectedCategory === 'visited' ? '600' : '500'
                              }}
                              onMouseEnter={(e) => {
                                if (selectedCategory !== 'visited') {
                                  e.currentTarget.style.background = '#f3f4f6';
                                }
                              }}
                              onMouseLeave={(e) => {
                                if (selectedCategory !== 'visited') {
                                  e.currentTarget.style.background = 'transparent';
                                }
                              }}
                            >
                              <i className="bi bi-bookmark-fill" style={{ width: '1.2rem', textAlign: 'center', marginRight: '.5rem', display: 'inline-block' }}></i> Visited
                            </button>
                          </li>
                        )}
                        <li>
                          <button
                            type="button"
                            onClick={() => handleCategorySelect('all')}
                            className="w-full text-left"
                            style={{
                              display: 'block',
                              width: '100%',
                              padding: '.5rem 1rem',
                              textAlign: 'left',
                              background: selectedCategory === 'all' ? '#0B4778' : 'transparent',
                              color: selectedCategory === 'all' ? '#fff' : '#111827',
                              border: 'none',
                              cursor: 'pointer',
                              fontSize: '.875rem',
                              fontWeight: selectedCategory === 'all' ? '600' : '500'
                            }}
                            onMouseEnter={(e) => {
                              if (selectedCategory !== 'all') {
                                e.currentTarget.style.background = '#f3f4f6';
                              }
                            }}
                            onMouseLeave={(e) => {
                              if (selectedCategory !== 'all') {
                                e.currentTarget.style.background = 'transparent';
                              }
                            }}
                          >
                            <i className="bi bi-grid-fill" style={{ width: '1.2rem', textAlign: 'center', marginRight: '.5rem', display: 'inline-block' }}></i> All categories
                          </button>
                        </li>
                        <li><hr style={{ margin: '.25rem 0', border: 'none', borderTop: '1px solid #e5e7eb' }} /></li>
                        {businessCategories.map((category, index) => (
                          <React.Fragment key={category.id}>
                            {category.name === 'Others' && index > 0 && (
                              <li><hr style={{ margin: '.25rem 0', border: 'none', borderTop: '1px solid #e5e7eb' }} /></li>
                            )}
                            <li>
                              <button
                                type="button"
                                onClick={() => handleCategorySelect(category.id)}
                                className="w-full text-left"
                                style={{
                                  display: 'block',
                                  width: '100%',
                                  padding: '.5rem 1rem',
                                  textAlign: 'left',
                                  background: selectedCategory === category.id ? '#0B4778' : 'transparent',
                                  color: selectedCategory === category.id ? '#fff' : '#111827',
                                  border: 'none',
                                  cursor: 'pointer',
                                  fontSize: '.875rem',
                                  whiteSpace: 'nowrap'
                                }}
                              onMouseEnter={(e) => {
                                if (selectedCategory !== category.id) {
                                  e.currentTarget.style.background = '#f3f4f6';
                                }
                              }}
                              onMouseLeave={(e) => {
                                if (selectedCategory !== category.id) {
                                  e.currentTarget.style.background = 'transparent';
                                }
                              }}
                            >
                              <i className="bi bi-tag" style={{ width: '1.2rem', textAlign: 'center', marginRight: '.5rem', display: 'inline-block' }}></i> {category.name}
                            </button>
                          </li>
                          </React.Fragment>
                        ))}
                      </ul>
                    )}
                  </div>

                  {/* Search Input */}
                  <input
                    ref={desktopSearchInputRef}
                    type="search"
                    className="form-control w-full md:flex-1 rounded-lg md:rounded-none border md:border-0 border-gray-300 focus:border-gray-300 focus:ring-0"
                    placeholder="Search service, business, city, or phone..."
                    aria-label="Search service, business, city, or phone"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        handleSearch();
                      }
                    }}
                    style={{
                      boxShadow: 'none',
                      fontSize: '.95rem',
                      paddingLeft: '1rem',
                      paddingRight: '1rem',
                      paddingTop: '.5rem',
                      paddingBottom: '.5rem',
                      minWidth: '0',
                      outline: 'none'
                    }}
                  />

                  {/* Search Button */}
                  <button
                    className="aacore-btn-primary w-full md:w-auto justify-center" 
                    type="submit"
                    style={{
                      whiteSpace: 'nowrap'
                    }}
                  >
                    <i className="bi bi-search" aria-hidden="true"></i><span>Search</span>
                  </button>
                </div>
              </div>
            </form>
            
            {/* Learn about AACore link */}
            <div className="flex justify-center mb-4">
              <div className="w-full max-w-3xl flex justify-center">
                <AnimatedLink
                  to="/features/"
                  className="inline-flex items-center gap-2 text-sm font-medium transition-colors text-[#0B4778] hover:text-[#0a3d66]"
                >
                  <i className="bi bi-info-circle text-base"></i>
                  <span>Learn about AACore for Business</span>
                </AnimatedLink>
              </div>
            </div>
          </div>

          {/* Businesses List */}
          <div className="flex justify-center">
            <div className="w-full max-w-3xl">
              {isLoading ? (
                <div className="flex justify-center items-center py-12">
                  <LoadingSpinner />
                </div>
              ) : filteredBusinesses.length === 0 ? (
                <div className="bg-white border border-gray-200 rounded-lg p-12 shadow-sm text-center">
                  <div className="mx-auto h-16 w-16 flex items-center justify-center rounded-full bg-gray-100 mb-4">
                    <Search className="h-8 w-8 text-gray-400" />
                  </div>
                  <h3 className="text-xl font-semibold text-gray-900 mb-2">
                    {emptyState.title}
                  </h3>
                  <p className="text-gray-600">
                    {emptyState.message}
                  </p>
                </div>
              ) : (
                <div>
                  {filteredBusinesses.map((business) => (
                    <BusinessResultItem
                      key={business.id}
                      business={business}
                      getLogoUrl={getLogoUrl}
                      getBusinessInitials={getBusinessInitials}
                    />
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Footer */}
      <Footer />
    </div>
  );
};

export default SearchPage;


